import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { OnboardingTable } from '@/components/ui/onboarding-table';
import { cn } from '@/lib/utils';
import type { TestScriptCandidate } from '@/components/smartai/api/getScriptCandidates';
import ScriptSelectionTreeView from './ScriptSelectionTreeView';
import {
  applyFileToggle,
  applyFolderToggle,
  buildSelectionTreeFromCandidates,
  collectAllFolderPathKeys,
  collectFolderKeysForFilter,
  displayTreeRoots,
  filterSelectionTree,
  folderCheckState,
} from './scriptSelectionTree';

interface TestScriptSelectionCardProps {
  candidates: TestScriptCandidate[];
  selectedPaths: Set<string>;
  onSelectionChange: (paths: Set<string>) => void;
  loading?: boolean;
  error?: string | null;
  unresolvedCount?: number;
}

interface ScriptRow extends TestScriptCandidate {
  _key: string;
}

type ViewMode = 'folders' | 'list';

const scriptMessageClass =
  'my-4 text-sm text-[var(--inv-text-secondary)]';

export default function TestScriptSelectionCard({
  candidates,
  selectedPaths,
  onSelectionChange,
  loading = false,
  error = null,
}: TestScriptSelectionCardProps) {
  const [filter, setFilter] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('folders');
  const [expandedDirs, setExpandedDirs] = useState<Set<string>>(new Set());

  const fullTree = useMemo(
    () => displayTreeRoots(buildSelectionTreeFromCandidates(candidates)),
    [candidates],
  );

  const filteredTree = useMemo(
    () => filterSelectionTree(fullTree, filter),
    [fullTree, filter],
  );

  const filteredList: ScriptRow[] = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const rows = candidates.map(c => ({ ...c, _key: c.filePath }));
    if (!q) return rows;
    return rows.filter(
      r =>
        r.filePath.toLowerCase().includes(q) ||
        r.fileName.toLowerCase().includes(q),
    );
  }, [candidates, filter]);

  useEffect(() => {
    if (!candidates.length) return;
    const keys = filter.trim()
      ? collectFolderKeysForFilter(filteredTree)
      : new Set<string>();
    setExpandedDirs(keys);
  }, [candidates.length, filter, fullTree, filteredTree]);

  const selectedCount = selectedPaths.size;
  const allPaths = useMemo(
    () => candidates.map(c => c.filePath),
    [candidates],
  );
  const rootCheckState = folderCheckState(allPaths, selectedPaths);

  const toggleAll = (checked: boolean) => {
    onSelectionChange(checked ? new Set(allPaths) : new Set());
  };

  const toggleFile = (path: string, checked: boolean) => {
    onSelectionChange(applyFileToggle(path, selectedPaths, checked));
  };

  const toggleFolder = (descendantPaths: string[], checked: boolean) => {
    onSelectionChange(
      applyFolderToggle(descendantPaths, selectedPaths, checked),
    );
  };

  const toggleDir = (pathKey: string) => {
    setExpandedDirs(prev => {
      const next = new Set(prev);
      if (next.has(pathKey)) next.delete(pathKey);
      else next.add(pathKey);
      return next;
    });
  };

  const expandAllFolders = () => {
    setExpandedDirs(collectAllFolderPathKeys(fullTree));
  };

  const collapseAllFolders = () => {
    setExpandedDirs(new Set());
  };

  const listAllChecked =
    filteredList.length > 0 &&
    filteredList.every(r => selectedPaths.has(r.filePath));
  const listIndeterminate =
    !listAllChecked &&
    filteredList.some(r => selectedPaths.has(r.filePath));

  if (loading) {
    return <p className={scriptMessageClass}>Loading test scripts…</p>;
  }

  if (error) {
    return (
      <p className={`${scriptMessageClass} text-[#c62828]`}>{error}</p>
    );
  }

  if (!candidates.length) {
    return (
      <p className={scriptMessageClass}>
        No scripts found. Check release pipelines, environments, and test repo
        branch.
      </p>
    );
  }

  return (
    <div className="mt-6 rounded-[14px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] p-4 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]">
      <h3 className="mb-2 text-[1.1rem] font-semibold text-[var(--inv-text)]">
        Test script selection
      </h3>
      <p className="mb-4 text-sm text-[var(--inv-text-secondary)]">
        Browse folders and select scripts to index. Checking a folder selects all
        scripts inside it. Nothing is selected until you choose files or folders.
      </p>

      <div className="mb-3 flex items-center gap-4">
        <Input
          type="search"
          className="flex-1"
          placeholder="Filter by folder or file…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
        <span className="whitespace-nowrap text-sm text-[var(--inv-text-secondary)]">
          {selectedCount} of {candidates.length} selected
        </span>
      </div>

      <div className="mb-3 flex flex-wrap gap-1 rounded-lg p-0.5">
        <Button
          variant={viewMode === 'folders' ? 'default' : 'ghost'}
          size="sm"
          type="button"
          className={cn(
            'h-8 flex-1 sm:flex-none',
            viewMode !== 'folders' && 'text-[var(--inv-text-secondary)]',
          )}
          onClick={() => setViewMode('folders')}>
          Folder view
        </Button>
        <Button
          variant={viewMode === 'list' ? 'default' : 'ghost'}
          size="sm"
          type="button"
          className={cn(
            'h-8 flex-1 sm:flex-none',
            viewMode !== 'list' && 'text-[var(--inv-text-secondary)]',
          )}
          onClick={() => setViewMode('list')}>
          List view
        </Button>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-0 border-b border-solid border-[var(--inv-border-soft)] pb-3">
        <Checkbox
          checked={rootCheckState === 'checked'}
          indeterminate={rootCheckState === 'indeterminate'}
          onChange={val => toggleAll(val || rootCheckState === 'indeterminate')}
          className="text-[0.8125rem]">
          <span>Select all scripts</span>
        </Checkbox>
        {viewMode === 'folders' && (
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="h-8 px-2.5 text-[0.75rem] text-[var(--inv-text-secondary)]"
              onClick={expandAllFolders}>
              Expand all
            </Button>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="h-8 px-2.5 text-[0.75rem] text-[var(--inv-text-secondary)]"
              onClick={collapseAllFolders}>
              Collapse all
            </Button>
          </div>
        )}
      </div>

      <div className="max-h-[420px] overflow-auto rounded-[10px] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-1.5 py-1">
        {viewMode === 'folders' ? (
          filteredTree.length > 0 ? (
            <ScriptSelectionTreeView
              nodes={filteredTree}
              expandedDirs={expandedDirs}
              onToggleDir={toggleDir}
              selectedPaths={selectedPaths}
              onFolderToggle={toggleFolder}
              onFileToggle={toggleFile}
            />
          ) : (
            <p className={scriptMessageClass}>No folders match your filter.</p>
          )
        ) : (
          <div className="overflow-y-auto">
            <OnboardingTable
              className="mb-0 rounded-none border-0 shadow-none"
              data={filteredList}
              rowKey={row => row.filePath}
              selectedKeys={selectedPaths}
              columns={[
                {
                  key: 'path',
                  header: (
                    <Checkbox
                      checked={listAllChecked}
                      indeterminate={listIndeterminate}
                      onChange={val => {
                        const next = new Set(selectedPaths);
                        for (const row of filteredList) {
                          if (val) next.add(row.filePath);
                          else next.delete(row.filePath);
                        }
                        onSelectionChange(next);
                      }}>
                      <span>Script path</span>
                    </Checkbox>
                  ),
                  render: (row: ScriptRow) => (
                    <Checkbox
                      checked={selectedPaths.has(row.filePath)}
                      onChange={val => toggleFile(row.filePath, val)}>
                      <span className="break-all text-[0.8rem]">
                        {row.filePath}
                      </span>
                    </Checkbox>
                  ),
                },
                {
                  key: 'fileName',
                  header: 'File',
                  render: (row: ScriptRow) => row.fileName,
                },
              ]}
            />
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="h-8"
          onClick={() => onSelectionChange(new Set(allPaths))}>
          Select all
        </Button>
        <Button
          variant="outline"
          size="sm"
          type="button"
          className="h-8"
          onClick={() => onSelectionChange(new Set())}>
          Clear all
        </Button>
      </div>
    </div>
  );
}
